import React, { useRef, useEffect, useState } from 'react';
import './Shuffle.css';

const Shuffle = ({
  text,
  className = '',
  style = {},
  tag = 'h1',
  textAlign = 'center',
  continuous = true,
  chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*',
  maxIterations = 12,
  shuffleSpeed = 90,
  minDelay = 2000,
  maxDelay = 3500
}) => {
  const shuffleTextRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const shuffleIntervalRef = useRef(null);
  const isShufflingRef = useRef(false);
  const nextShuffleTimeoutRef = useRef(null);

  useEffect(() => {
    if (!shuffleTextRef.current || !text) return;

    const shuffleText = shuffleTextRef.current;
    const characters = text.split('');
    const spans = [];

    // Clear previous content
    shuffleText.innerHTML = '';

    // Create character spans
    characters.forEach((char, index) => {
      const span = document.createElement('span');
      span.className = 'shuffle-char active';
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.setAttribute('data-original', char);
      shuffleText.appendChild(span);
      spans.push(span);
    });

    setIsReady(true);

    function startShuffling() {
      if (isShufflingRef.current) return;
      isShufflingRef.current = true;

      let iterations = 0;

      shuffleIntervalRef.current = setInterval(() => {
        let allResolved = true;

        spans.forEach((span, i) => {
          const originalChar = characters[i];

          if (originalChar === ' ') return;

          const threshold = iterations / maxIterations;
          const charResolvePoint = Math.random();

          if (charResolvePoint > threshold) {
            span.textContent = chars[Math.floor(Math.random() * chars.length)];
            allResolved = false;
          } else {
            span.textContent = originalChar;
          }
        });

        iterations++;

        if (iterations >= maxIterations || allResolved) {
          // Ensure all characters show original text
          spans.forEach((span, i) => {
            span.textContent = characters[i] === ' ' ? '\u00A0' : characters[i];
          });

          clearInterval(shuffleIntervalRef.current);
          isShufflingRef.current = false;

          // Add glitch effect
          shuffleText.classList.add('glitch');
          setTimeout(() => {
            shuffleText.classList.remove('glitch');
          }, 200);

          // Schedule next shuffle if continuous
          if (continuous) {
            nextShuffleTimeoutRef.current = setTimeout(() => {
              startShuffling();
            }, minDelay + Math.random() * (maxDelay - minDelay));
          }
        }
      }, shuffleSpeed);
    }

    // Start first shuffle after brief delay
    const initialTimeout = setTimeout(startShuffling, 300);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      if (shuffleIntervalRef.current) {
        clearInterval(shuffleIntervalRef.current);
      }
      if (nextShuffleTimeoutRef.current) {
        clearTimeout(nextShuffleTimeoutRef.current);
      }
    };
  }, [text, continuous, chars, maxIterations, shuffleSpeed, minDelay, maxDelay]);

  const Tag = tag || 'h1';

  return (
    <div className="shuffle-wrapper" style={{ textAlign, ...style }}>
      <Tag
        ref={shuffleTextRef}
        className={`shuffle-text ${className} ${isReady ? 'is-ready' : ''}`}
        data-text={text}
      >
        {/* Characters will be dynamically inserted */}
      </Tag>
    </div>
  );
};

export default Shuffle;

